/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteConsoleTabPathParameters,
  TLBulkSaveConsoleTabsRequest,
  TLConsoleTabResponse,
  TLConsoleTabsListResponse,
  TLCreateConsoleTabRequest,
  TLHTTPValidationError,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Console Tabs
 */
export const getConsoleTabs = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLConsoleTabsListResponse>({ url: `/foundry/console-tabs`, method: 'GET', signal }, options);
};

export const getGetConsoleTabsQueryKey = () => {
  return ['foundry', 'console-tabs'] as const;
};

export const getGetConsoleTabsQueryOptions = <
  TData = Awaited<ReturnType<typeof getConsoleTabs>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConsoleTabs>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConsoleTabsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConsoleTabs>>> = ({ signal }) =>
    getConsoleTabs(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getConsoleTabs>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetConsoleTabsQueryResult = NonNullable<Awaited<ReturnType<typeof getConsoleTabs>>>;
export type GetConsoleTabsQueryError = ErrorType<unknown>;

export function useGetConsoleTabs<TData = Awaited<ReturnType<typeof getConsoleTabs>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConsoleTabs>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConsoleTabs>>,
          TError,
          Awaited<ReturnType<typeof getConsoleTabs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConsoleTabs<TData = Awaited<ReturnType<typeof getConsoleTabs>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConsoleTabs>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConsoleTabs>>,
          TError,
          Awaited<ReturnType<typeof getConsoleTabs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConsoleTabs<TData = Awaited<ReturnType<typeof getConsoleTabs>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConsoleTabs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Console Tabs
 */

export function useGetConsoleTabs<TData = Awaited<ReturnType<typeof getConsoleTabs>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConsoleTabs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConsoleTabsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Console Tabs
 */
export const prefetchGetConsoleTabsQuery = async <
  TData = Awaited<ReturnType<typeof getConsoleTabs>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConsoleTabs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConsoleTabsQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Console Tabs
 */
export const invalidateGetConsoleTabs = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConsoleTabsQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Create Console Tab
 */
export const createConsoleTab = (
  tLCreateConsoleTabRequest: BodyType<TLCreateConsoleTabRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConsoleTabResponse>(
    {
      url: `/foundry/console-tabs`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCreateConsoleTabRequest,
      signal,
    },
    options,
  );
};

export const getCreateConsoleTabQueryKey = (tLCreateConsoleTabRequest?: BodyType<TLCreateConsoleTabRequest>) => {
  return ['POST', 'foundry', 'console-tabs', tLCreateConsoleTabRequest] as const;
};

export const getCreateConsoleTabQueryOptions = <
  TData = Awaited<ReturnType<typeof createConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateConsoleTabRequest: BodyType<TLCreateConsoleTabRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConsoleTab>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateConsoleTabQueryKey(tLCreateConsoleTabRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createConsoleTab>>> = ({ signal }) =>
    createConsoleTab(tLCreateConsoleTabRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createConsoleTab>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateConsoleTabQueryResult = NonNullable<Awaited<ReturnType<typeof createConsoleTab>>>;
export type CreateConsoleTabQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateConsoleTab<
  TData = Awaited<ReturnType<typeof createConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateConsoleTabRequest: BodyType<TLCreateConsoleTabRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConsoleTab>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createConsoleTab>>,
          TError,
          Awaited<ReturnType<typeof createConsoleTab>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateConsoleTab<
  TData = Awaited<ReturnType<typeof createConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateConsoleTabRequest: BodyType<TLCreateConsoleTabRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConsoleTab>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createConsoleTab>>,
          TError,
          Awaited<ReturnType<typeof createConsoleTab>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateConsoleTab<
  TData = Awaited<ReturnType<typeof createConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateConsoleTabRequest: BodyType<TLCreateConsoleTabRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConsoleTab>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Console Tab
 */

export function useCreateConsoleTab<
  TData = Awaited<ReturnType<typeof createConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateConsoleTabRequest: BodyType<TLCreateConsoleTabRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConsoleTab>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateConsoleTabQueryOptions(tLCreateConsoleTabRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Console Tab
 */
export const prefetchCreateConsoleTabQuery = async <
  TData = Awaited<ReturnType<typeof createConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLCreateConsoleTabRequest: BodyType<TLCreateConsoleTabRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConsoleTab>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateConsoleTabQueryOptions(tLCreateConsoleTabRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Console Tab
 */
export const invalidateCreateConsoleTab = async (
  queryClient: QueryClient,
  tLCreateConsoleTabRequest: BodyType<TLCreateConsoleTabRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateConsoleTabQueryKey(tLCreateConsoleTabRequest) }, options);

  return queryClient;
};

/**
 * @summary Bulk Save Console Tabs
 */
export const bulkSaveConsoleTabs = (
  tLBulkSaveConsoleTabsRequest: BodyType<TLBulkSaveConsoleTabsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConsoleTabsListResponse>(
    {
      url: `/foundry/console-tabs`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLBulkSaveConsoleTabsRequest,
      signal,
    },
    options,
  );
};

export const getBulkSaveConsoleTabsQueryKey = (
  tLBulkSaveConsoleTabsRequest?: BodyType<TLBulkSaveConsoleTabsRequest>,
) => {
  return ['PUT', 'foundry', 'console-tabs', tLBulkSaveConsoleTabsRequest] as const;
};

export const getBulkSaveConsoleTabsQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkSaveConsoleTabs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkSaveConsoleTabsRequest: BodyType<TLBulkSaveConsoleTabsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSaveConsoleTabs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkSaveConsoleTabsQueryKey(tLBulkSaveConsoleTabsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkSaveConsoleTabs>>> = ({ signal }) =>
    bulkSaveConsoleTabs(tLBulkSaveConsoleTabsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkSaveConsoleTabs>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkSaveConsoleTabsQueryResult = NonNullable<Awaited<ReturnType<typeof bulkSaveConsoleTabs>>>;
export type BulkSaveConsoleTabsQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkSaveConsoleTabs<
  TData = Awaited<ReturnType<typeof bulkSaveConsoleTabs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkSaveConsoleTabsRequest: BodyType<TLBulkSaveConsoleTabsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSaveConsoleTabs>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkSaveConsoleTabs>>,
          TError,
          Awaited<ReturnType<typeof bulkSaveConsoleTabs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkSaveConsoleTabs<
  TData = Awaited<ReturnType<typeof bulkSaveConsoleTabs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkSaveConsoleTabsRequest: BodyType<TLBulkSaveConsoleTabsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSaveConsoleTabs>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkSaveConsoleTabs>>,
          TError,
          Awaited<ReturnType<typeof bulkSaveConsoleTabs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkSaveConsoleTabs<
  TData = Awaited<ReturnType<typeof bulkSaveConsoleTabs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkSaveConsoleTabsRequest: BodyType<TLBulkSaveConsoleTabsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSaveConsoleTabs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Save Console Tabs
 */

export function useBulkSaveConsoleTabs<
  TData = Awaited<ReturnType<typeof bulkSaveConsoleTabs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkSaveConsoleTabsRequest: BodyType<TLBulkSaveConsoleTabsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSaveConsoleTabs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkSaveConsoleTabsQueryOptions(tLBulkSaveConsoleTabsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Save Console Tabs
 */
export const prefetchBulkSaveConsoleTabsQuery = async <
  TData = Awaited<ReturnType<typeof bulkSaveConsoleTabs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLBulkSaveConsoleTabsRequest: BodyType<TLBulkSaveConsoleTabsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSaveConsoleTabs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkSaveConsoleTabsQueryOptions(tLBulkSaveConsoleTabsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Save Console Tabs
 */
export const invalidateBulkSaveConsoleTabs = async (
  queryClient: QueryClient,
  tLBulkSaveConsoleTabsRequest: BodyType<TLBulkSaveConsoleTabsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkSaveConsoleTabsQueryKey(tLBulkSaveConsoleTabsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Console Tab
 */
export const deleteConsoleTab = (
  { tabId }: DeleteConsoleTabPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/foundry/console-tabs/${tabId}`, method: 'DELETE', signal }, options);
};

export const getDeleteConsoleTabQueryKey = ({ tabId }: DeleteConsoleTabPathParameters) => {
  return ['DELETE', 'foundry', 'console-tabs', tabId] as const;
};

export const getDeleteConsoleTabQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tabId }: DeleteConsoleTabPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConsoleTab>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteConsoleTabQueryKey({ tabId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteConsoleTab>>> = ({ signal }) =>
    deleteConsoleTab({ tabId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: tabId !== null && tabId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteConsoleTab>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteConsoleTabQueryResult = NonNullable<Awaited<ReturnType<typeof deleteConsoleTab>>>;
export type DeleteConsoleTabQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteConsoleTab<
  TData = Awaited<ReturnType<typeof deleteConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConsoleTabPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConsoleTab>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteConsoleTab>>,
          TError,
          Awaited<ReturnType<typeof deleteConsoleTab>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteConsoleTab<
  TData = Awaited<ReturnType<typeof deleteConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConsoleTabPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConsoleTab>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteConsoleTab>>,
          TError,
          Awaited<ReturnType<typeof deleteConsoleTab>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteConsoleTab<
  TData = Awaited<ReturnType<typeof deleteConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConsoleTabPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConsoleTab>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Console Tab
 */

export function useDeleteConsoleTab<
  TData = Awaited<ReturnType<typeof deleteConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { tabId }: DeleteConsoleTabPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConsoleTab>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteConsoleTabQueryOptions({ tabId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Console Tab
 */
export const prefetchDeleteConsoleTabQuery = async <
  TData = Awaited<ReturnType<typeof deleteConsoleTab>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { tabId }: DeleteConsoleTabPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConsoleTab>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteConsoleTabQueryOptions({ tabId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Console Tab
 */
export const invalidateDeleteConsoleTab = async (
  queryClient: QueryClient,
  { tabId }: DeleteConsoleTabPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteConsoleTabQueryKey({ tabId }) }, options);

  return queryClient;
};
